import React, { useEffect, useRef, useState, memo } from 'react';
import { createKiosk } from './kioskController';
import { BASE } from './hrApi';
import './Kiosk.css';

const LOGO = 'https://fotileams.com/fotile-logo-red.png';
const TOKEN_KEY = 'kiosk_token';

const readToken = () => { try { return localStorage.getItem(TOKEN_KEY) || ''; } catch (_) { return ''; } };
const writeToken = (t) => { try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch (_) { /* private mode */ } };

/* The markup below is driven by kioskController.js (it finds elements by data-k). It is memoised and takes
   no changing props, so React never re-renders over what the camera loop writes into it. */
const KioskView = memo(function KioskView({ token, onAuthLost, onSignOut }) {
  const rootRef = useRef(null);
  useEffect(() => {
    const k = createKiosk({ root: rootRef.current, token, onToken: writeToken, onAuthLost, onSignOut });
    k.start();
    return () => k.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fk-root" ref={rootRef}>
      <div className="fk-loading" data-k="loading">Starting camera and face models…</div>

      <main className="fk-stage" data-k="stage" data-mode="idle" data-tone="">
        <video className="fk-video" data-k="video" playsInline muted autoPlay />
        <canvas className="fk-overlay" data-k="overlay" />
        <div className="fk-shade" />

        <div className="fk-ringbox" aria-hidden="true">
          <svg viewBox="-4 -4 308 408">
            <ellipse className="fk-ring-track" cx="150" cy="200" rx="150" ry="200" />
            <ellipse className="fk-ring-fill" data-k="ring-fill" cx="150" cy="200" rx="150" ry="200" pathLength="100" />
          </svg>
        </div>

        <header className="fk-top">
          <div className="fk-where" data-k="where">
            <img src={LOGO} alt="" className="fk-logo" />
            <div><b data-k="dev-name" /><span data-k="dev-loc" /></div>
          </div>
          <div className="fk-clock"><div className="fk-time" data-k="time">--:--</div><div className="fk-date" data-k="date" /></div>
        </header>

        <section className="fk-result" aria-live="assertive">
          <div className="fk-res-kind" data-k="res-kind" />
          <div className="fk-res-name" data-k="res-name" />
          <div className="fk-res-meta" data-k="res-meta" />
          <div className="fk-res-late" data-k="res-late" hidden />
        </section>

        <footer className="fk-bottom" aria-live="polite">
          <div className="fk-prompt" data-k="prompt">Look at the camera</div>
          <div className="fk-sub" data-k="sub" />
          <div className="fk-steps" data-k="steps" />
        </footer>

        <div className="fk-shadowbar" data-k="shadowbar">TEST MODE — attendance is not being recorded yet</div>
        <div className="fk-banner" data-k="banner" role="alert" />
        <pre className="fk-hud" data-k="hud" />
      </main>

      <div className="fk-setup" data-k="setup" role="dialog" aria-modal="true">
        <div className="fk-setup-box">
          <h1>Kiosk settings</h1>
          <p>These options apply to this device only.</p>
          <div className="fk-opts">
            <label><input data-k="opt-sound" type="checkbox" /> Play sounds</label>
            <label><input data-k="opt-speak" type="checkbox" /> Speak the person&apos;s first name</label>
            <label><input data-k="opt-debug" type="checkbox" /> Show detection overlay (landmarks, pose, age, gender, emotion)</label>
          </div>
          <div className="fk-setup-btns">
            <button data-k="setup-signout" type="button" className="fk-danger">Sign this device out</button>
            <span style={{ flex: 1 }} />
            <button data-k="setup-cancel" type="button">Cancel</button>
            <button data-k="setup-go" type="button" className="fk-go">Save</button>
          </div>
          <p className="fk-hint">Shortcuts: <b>S</b> settings · <b>D</b> overlay · <b>F</b> full screen. On a touch screen, press and hold the device name.</p>
        </div>
      </div>
    </div>
  );
});

function KioskLogin({ message, onToken }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(message || '');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${BASE}/face/device/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Sign-in failed');
      onToken(data.token);
    } catch (ex) { setErr(ex.message === 'Failed to fetch' ? 'Cannot reach the server' : ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fk-login">
      <form className="fk-login-card" onSubmit={submit}>
        <img src={LOGO} alt="Fotile" className="fk-login-logo" />
        <h1>Attendance kiosk</h1>
        <p>Sign in with the device account HR created for this screen.</p>
        <label htmlFor="fk-u">Device username</label>
        <input id="fk-u" value={username} onChange={(e) => setUsername(e.target.value)} autoCapitalize="off" autoCorrect="off" spellCheck="false" autoFocus />
        <label htmlFor="fk-p">Password</label>
        <input id="fk-p" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {err && <div className="fk-login-err">{err}</div>}
        <button type="submit" disabled={busy || !username || !password}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
    </div>
  );
}

export default function Kiosk() {
  const [token, setToken] = useState(readToken());
  const [msg, setMsg] = useState('');

  const signedOut = (m) => { writeToken(''); setMsg(m || ''); setToken(''); };

  useEffect(() => {
    const prev = document.title;
    document.title = 'Fotile attendance kiosk';
    return () => { document.title = prev; };
  }, []);

  if (!token) return <KioskLogin message={msg} onToken={(t) => { writeToken(t); setMsg(''); setToken(t); }} />;
  return <KioskView key={token.slice(-12)} token={token} onAuthLost={signedOut} onSignOut={() => signedOut('')} />;
}
